import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { Project } from "./Project";
import { Milestone } from "./Milestone";
import { fetchMilestoneByIdAsync, fetchProjectsAsync } from "./projectApi";
import { ChartPoint } from "../chart/ChartPoint";
import { ProjectState } from "./ProjectState";

const defaultProject: Project = {
  id: 0,
  name: "",
  milestones: [],
};

const defaultMilestone: Milestone = {
  id: 0,
  name: "",
  totalEstimate: [],
  actualBurnUp: [],
  estimateBurnUp: [],
  estimatedTotalHoursNeeded: [],
  totalTaskEstimates: [],
  totalOfEstimatedCompleted: [],
  totalTasks: [],
  state: "",
  gitLabCreateDate: "",
  isFetchingCompleted: false,
  openTaskCount: 0,
  totalTaskCount: 0,
  forecast: null,
  forecastHistory: [],
};

const initialState: ProjectState = {
  projects: [],
  selectedProject: defaultProject,
  selectedMilestone: defaultMilestone,
  initializationStatus: "not-initialized",
  status: "idle",
  milestoneLoadingStatus: "idle",
};

const filterLatestChartPoints = (chartPoints: ChartPoint[]): ChartPoint[] => {
  const groupedByDate = chartPoints.reduce<Record<string, ChartPoint>>(
    (acc, point) => {
      const dateKey = new Date(point.date).toLocaleDateString();

      if (
        !acc[dateKey] ||
        new Date(point.dateCreated) > new Date(acc[dateKey].dateCreated)
      ) {
        acc[dateKey] = point;
      }

      return acc;
    },
    {}
  );

  return Object.values(groupedByDate);
};

const projectSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    setSelectedProject: (state, action: PayloadAction<Project>) => {
      state.selectedProject = action.payload;
    },
    setSelectedMilestone: (state, action: PayloadAction<Milestone>) => {
      state.selectedMilestone = action.payload;
    },
    resetSelection: (state) => {
      state.selectedProject = defaultProject;
      state.selectedMilestone = defaultMilestone;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectsAsync.pending, (state) => {
        state.initializationStatus = "initializing";
        state.status = "loading";
      })
      .addCase(fetchProjectsAsync.fulfilled, (state, action) => {
        state.projects = action.payload.map((project: Project) => ({
          ...project,
          milestones: project.milestones
            .map((milestone: Milestone) => ({
              ...milestone,
            }))
            .sort(
              (a, b) =>
                new Date(b.gitLabCreateDate).getTime() -
                new Date(a.gitLabCreateDate).getTime()
            ),
        }));
        state.initializationStatus = "initialized";
        state.status = "idle";
      })
      .addCase(fetchProjectsAsync.rejected, (state) => {
        state.initializationStatus = "initialized";
        state.status = "idle";
      })
      .addCase(fetchMilestoneByIdAsync.pending, (state) => {
        state.status = "loading";
        state.milestoneLoadingStatus = "loading";
      })
      .addCase(fetchMilestoneByIdAsync.fulfilled, (state, action) => {
        const fetchedMilestone = action.payload;
        const filteredMilestone = {
          ...fetchedMilestone,
          totalEstimate: filterLatestChartPoints(
            fetchedMilestone.totalEstimate
          ),
          actualBurnUp: filterLatestChartPoints(fetchedMilestone.actualBurnUp),
          estimateBurnUp: filterLatestChartPoints(
            fetchedMilestone.estimateBurnUp
          ),
          estimatedTotalHoursNeeded: filterLatestChartPoints(
            fetchedMilestone.estimatedTotalHoursNeeded
          ),
          totalTaskEstimates: filterLatestChartPoints(
            fetchedMilestone.totalTaskEstimates
          ),
          totalOfEstimatedCompleted: filterLatestChartPoints(
            fetchedMilestone.totalOfEstimatedCompleted
          ),
          totalTasks: filterLatestChartPoints(fetchedMilestone.totalTasks),
          isFetchingCompleted: true,
        };
        state.status = "idle";
        state.selectedMilestone = filteredMilestone;
        state.milestoneLoadingStatus = "completed";

        const projectIndex = state.projects.findIndex((p) =>
          p.milestones.some((m) => m.id === fetchedMilestone.id)
        );

        if (projectIndex !== -1) {
          const milestoneIndex = state.projects[
            projectIndex
          ].milestones.findIndex((m) => m.id === fetchedMilestone.id);
          if (milestoneIndex !== -1) {
            state.projects[projectIndex].milestones[milestoneIndex] = {
              ...filteredMilestone,
              isFetchingCompleted: true,
            };
          }
        }
      })
      .addCase(fetchMilestoneByIdAsync.rejected, (state) => {
        state.status = "failed";
        state.milestoneLoadingStatus = "failed";
      });
  },
});

export const { setSelectedProject, setSelectedMilestone, resetSelection } =
  projectSlice.actions;
export const selectSelectedProject = (state: RootState) =>
  state.projects.selectedProject;
export const selectSelectedMilestone = (state: RootState) =>
  state.projects.selectedMilestone;
export const selectProjects = (state: RootState) => state.projects.projects;
export const selectInitializationStatus = (state: RootState) =>
  state.projects.initializationStatus;
export const selectMilestoneLoadingStatus = (state: RootState) =>
  state.projects.milestoneLoadingStatus;
export const selectIsMilestoneCompletedOrFailed = (state: RootState) =>
  state.projects.milestoneLoadingStatus === "completed" ||
  state.projects.milestoneLoadingStatus === "failed";

export const selectIsMilestoneLoading = (state: RootState) =>
  state.projects.milestoneLoadingStatus === "loading";

export const selectIsMilestoneLoadingCompleted = (state: RootState) =>
  state.projects.milestoneLoadingStatus === "completed";

export default projectSlice.reducer;
